/**
 * Structural + semantic validation of {@link ObjectDefinitionSpec} input.
 *
 * `parseObjectDefinitionSpec` takes UNTRUSTED JSON (GraphQL JSON scalar,
 * imported files) and returns either a fully typed spec (rebuilt field by
 * field — unknown keys are dropped) or the list of issues. The same function
 * runs on the server (save/publish) and in the editor (inline messages).
 */

import {
  OBJECT_DEFINITION_LIMITS,
  OBJECT_DEFINITION_SCHEMA_VERSION,
  OBJECT_PARAM_KEY_PATTERN,
  OPENING_WALL_VARIABLE,
  DIMENSION_OFFSET_VARIABLE,
  isFormulaHandlePoint,
  type NumberBinding,
  type ObjectAxisResize,
  type ObjectDepthHandle,
  type ObjectDepthLink,
  type ObjectFormula,
  type ObjectHandlePoint,
  type ObjectParamAssignment,
  type ObjectStepSync,
  type ObjectDefinitionConstraint,
  type ObjectDefinitionSpec,
  type ObjectGroup,
  type ObjectHandles,
  type ObjectModel3D,
  type ObjectModelScaleMode,
  type ObjectModifier,
  type ObjectDimension,
  type ObjectOpening,
  type ObjectParam,
  type ObjectParamValue,
  type ObjectRepeat,
  type ObjectRepeatSkip,
  type ObjectShape,
  type ObjectShapeLayer,
  type ObjectShapeStyle,
  type ObjectVisibilityCondition,
  type Vec2,
  type Vec3,
} from './spec';
import { checkExpression, EXPRESSION_RESERVED_NAMES, EXPRESSION_SIZE_VARIABLES, MAX_EXPRESSION_LENGTH } from './expression';

export interface ObjectDefinitionIssue {
  /** Dotted path into the spec, e.g. `groups[2].repeat.pitch`. */
  path: string;
  message: string;
}

export type ObjectDefinitionParseResult = { ok: true; spec: ObjectDefinitionSpec } | { ok: false; issues: ObjectDefinitionIssue[] };

type UnknownRecord = Record<string, unknown>;

const AXIS_RESIZE: readonly ObjectAxisResize[] = ['stretch', 'fixed', 'pinStart', 'pinEnd', 'float'];
const REPEAT_SKIP: readonly ObjectRepeatSkip[] = ['none', 'first', 'last', 'both'];
const SHAPE_STYLES: readonly ObjectShapeStyle[] = ['solid', 'dashed'];
const SHAPE_LAYERS: readonly ObjectShapeLayer[] = ['glyph', 'selection'];
const MODEL_SCALE_MODES: readonly ObjectModelScaleMode[] = ['stretch', 'fixed', 'uniform'];
const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): value is T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value);
}

/** Collects issues while rebuilding typed values; every reader returns a safe fallback on failure. */
class Reader {
  readonly issues: ObjectDefinitionIssue[] = [];

  fail(path: string, message: string): void {
    this.issues.push({ path, message });
  }

  number(value: unknown, path: string, opts: { min?: number; max?: number; fallback?: number } = {}): number {
    if (!isFiniteNumber(value)) {
      this.fail(path, 'must be a finite number');
      return opts.fallback ?? 0;
    }
    if (opts.min !== undefined && value < opts.min) this.fail(path, `must be ≥ ${opts.min}`);
    if (opts.max !== undefined && value > opts.max) this.fail(path, `must be ≤ ${opts.max}`);
    return value;
  }

  optionalNumber(value: unknown, path: string): number | undefined {
    return value === undefined || value === null ? undefined : this.number(value, path);
  }

  string(value: unknown, path: string, maxLength: number = OBJECT_DEFINITION_LIMITS.maxLabelLength): string {
    if (typeof value !== 'string') {
      this.fail(path, 'must be a string');
      return '';
    }
    if (value.length > maxLength) this.fail(path, `must be at most ${maxLength} characters`);
    return value;
  }

  id(value: unknown, path: string): string {
    const id = this.string(value, path, 64);
    if (id !== '' && !ID_PATTERN.test(id)) this.fail(path, 'must only contain letters, digits, "_" or "-"');
    return id;
  }

  boolean(value: unknown, path: string): boolean {
    if (typeof value !== 'boolean') {
      this.fail(path, 'must be a boolean');
      return false;
    }
    return value;
  }

  optionalBoolean(value: unknown, path: string): boolean | undefined {
    return value === undefined || value === null ? undefined : this.boolean(value, path);
  }

  enumValue<T extends string>(value: unknown, allowed: readonly T[], path: string, fallback: T): T {
    if (!oneOf(value, allowed)) {
      this.fail(path, `must be one of ${allowed.join(', ')}`);
      return fallback;
    }
    return value;
  }

  vec2(value: unknown, path: string): Vec2 {
    if (!Array.isArray(value) || value.length !== 2) {
      this.fail(path, 'must be a [u, n] pair');
      return [0, 0];
    }
    return [this.number(value[0], `${path}[0]`), this.number(value[1], `${path}[1]`)];
  }

  vec3(value: unknown, path: string): Vec3 {
    if (!Array.isArray(value) || value.length !== 3) {
      this.fail(path, 'must be an [x, y, z] triple');
      return [0, 0, 0];
    }
    return [this.number(value[0], `${path}[0]`), this.number(value[1], `${path}[1]`), this.number(value[2], `${path}[2]`)];
  }

  array(value: unknown, path: string, maxLength: number): unknown[] {
    if (!Array.isArray(value)) {
      this.fail(path, 'must be an array');
      return [];
    }
    if (value.length > maxLength) this.fail(path, `must have at most ${maxLength} entries`);
    return value;
  }

  /** An expression's source text (syntax and names are checked with the rest of the spec). */
  expr(value: unknown, path: string): string {
    const src = this.string(value, path, MAX_EXPRESSION_LENGTH);
    if (src.trim() === '') this.fail(path, 'must not be empty');
    return src;
  }

  record(value: unknown, path: string): UnknownRecord {
    if (!isRecord(value)) {
      this.fail(path, 'must be an object');
      return {};
    }
    return value;
  }
}

function readConstraint(r: Reader, value: unknown): ObjectDefinitionConstraint {
  const rec = r.record(value, 'constraint');
  if (rec['kind'] === 'WALL') {
    const inset = r.optionalNumber(rec['defaultInset'], 'constraint.defaultInset');
    return inset === undefined ? { kind: 'WALL' } : { kind: 'WALL', defaultInset: inset };
  }
  if (rec['kind'] !== 'FREE') r.fail('constraint.kind', 'must be FREE or WALL');
  return { kind: 'FREE' };
}

function readParam(r: Reader, value: unknown, path: string): ObjectParam {
  const rec = r.record(value, path);
  const key = r.string(rec['key'], `${path}.key`, 32);
  if (!OBJECT_PARAM_KEY_PATTERN.test(key)) r.fail(`${path}.key`, 'must start with a letter and only contain letters and digits');
  const label = r.string(rec['label'], `${path}.label`);
  const order = r.optionalNumber(rec['order'], `${path}.order`);
  const base = { key, label, ...(order !== undefined ? { order } : {}) };
  switch (rec['kind']) {
    case 'float':
    case 'int': {
      const min = r.optionalNumber(rec['min'], `${path}.min`);
      const max = r.optionalNumber(rec['max'], `${path}.max`);
      const def = r.number(rec['default'], `${path}.default`);
      if (min !== undefined && max !== undefined && min > max) r.fail(`${path}.min`, 'must not exceed max');
      if ((min !== undefined && def < min) || (max !== undefined && def > max)) r.fail(`${path}.default`, 'must lie within min/max');
      if (rec['kind'] === 'int') {
        if (!Number.isInteger(def)) r.fail(`${path}.default`, 'must be a whole number');
        return { ...base, kind: 'int', default: def, ...(min !== undefined ? { min } : {}), ...(max !== undefined ? { max } : {}) };
      }
      const decimals = r.optionalNumber(rec['decimals'], `${path}.decimals`);
      const distance = r.optionalBoolean(rec['distance'], `${path}.distance`);
      return {
        ...base,
        kind: 'float',
        default: def,
        ...(min !== undefined ? { min } : {}),
        ...(max !== undefined ? { max } : {}),
        ...(decimals !== undefined ? { decimals } : {}),
        ...(distance !== undefined ? { distance } : {}),
      };
    }
    case 'boolean':
      return { ...base, kind: 'boolean', default: r.boolean(rec['default'], `${path}.default`) };
    case 'select': {
      const options = r.array(rec['options'], `${path}.options`, 32).map((option, i) => {
        const o = r.record(option, `${path}.options[${i}]`);
        return { value: r.string(o['value'], `${path}.options[${i}].value`), label: r.string(o['label'], `${path}.options[${i}].label`) };
      });
      if (options.length === 0) r.fail(`${path}.options`, 'must have at least one option');
      const def = r.string(rec['default'], `${path}.default`);
      if (options.length > 0 && !options.some((o) => o.value === def)) r.fail(`${path}.default`, 'must be one of the options');
      return { ...base, kind: 'select', default: def, options };
    }
    default:
      r.fail(`${path}.kind`, 'must be float, int, boolean or select');
      return { ...base, kind: 'boolean', default: false };
  }
}

function readShape(r: Reader, value: unknown, path: string): ObjectShape {
  const rec = r.record(value, path);
  const id = r.id(rec['id'], `${path}.id`);
  const name = rec['name'] === undefined ? undefined : r.string(rec['name'], `${path}.name`);
  const style = rec['style'] === undefined ? undefined : r.enumValue(rec['style'], SHAPE_STYLES, `${path}.style`, 'solid');
  const layer = rec['layer'] === undefined ? undefined : r.enumValue(rec['layer'], SHAPE_LAYERS, `${path}.layer`, 'glyph');
  const hidden = r.optionalBoolean(rec['hidden'], `${path}.hidden`);
  let expr: Record<string, string> | undefined;
  if (rec['expr'] !== undefined && rec['expr'] !== null) {
    const e = r.record(rec['expr'], `${path}.expr`);
    expr = {};
    for (const [field, src] of Object.entries(e)) expr[field] = r.expr(src, `${path}.expr.${field}`);
  }
  const base = {
    id,
    ...(name !== undefined ? { name } : {}),
    ...(style !== undefined ? { style } : {}),
    ...(layer !== undefined ? { layer } : {}),
    ...(hidden !== undefined ? { hidden } : {}),
    ...(expr && Object.keys(expr).length > 0 ? { expr } : {}),
  };
  switch (rec['kind']) {
    case 'polyline': {
      const points = r.array(rec['points'], `${path}.points`, OBJECT_DEFINITION_LIMITS.maxPoints).map((p, i) => r.vec2(p, `${path}.points[${i}]`));
      if (points.length < 2) r.fail(`${path}.points`, 'needs at least 2 points');
      return { ...base, kind: 'polyline', points, closed: r.boolean(rec['closed'], `${path}.closed`) };
    }
    case 'rect':
      return { ...base, kind: 'rect', min: r.vec2(rec['min'], `${path}.min`), max: r.vec2(rec['max'], `${path}.max`) };
    case 'circle':
      return { ...base, kind: 'circle', center: r.vec2(rec['center'], `${path}.center`), radius: r.number(rec['radius'], `${path}.radius`, { min: 0 }) };
    case 'arc':
      return {
        ...base,
        kind: 'arc',
        center: r.vec2(rec['center'], `${path}.center`),
        radius: r.number(rec['radius'], `${path}.radius`, { min: 0 }),
        startAngle: r.number(rec['startAngle'], `${path}.startAngle`),
        endAngle: r.number(rec['endAngle'], `${path}.endAngle`),
      };
    case 'path': {
      const nodes = r.array(rec['nodes'], `${path}.nodes`, OBJECT_DEFINITION_LIMITS.maxPoints).map((n, i) => {
        const node = r.record(n, `${path}.nodes[${i}]`);
        return {
          point: r.vec2(node['point'], `${path}.nodes[${i}].point`),
          ...(node['in'] !== undefined ? { in: r.vec2(node['in'], `${path}.nodes[${i}].in`) } : {}),
          ...(node['out'] !== undefined ? { out: r.vec2(node['out'], `${path}.nodes[${i}].out`) } : {}),
        };
      });
      if (nodes.length < 2) r.fail(`${path}.nodes`, 'needs at least 2 nodes');
      return { ...base, kind: 'path', nodes, closed: r.boolean(rec['closed'], `${path}.closed`) };
    }
    default:
      r.fail(`${path}.kind`, 'must be polyline, rect, circle, arc or path');
      return { ...base, kind: 'polyline', points: [], closed: false };
  }
}

function readBinding(r: Reader, value: unknown, path: string): NumberBinding {
  const rec = r.record(value, path);
  if (typeof rec['param'] === 'string') return { param: rec['param'] };
  if (rec['expr'] !== undefined) return { expr: r.expr(rec['expr'], `${path}.expr`) };
  return { value: r.number(rec['value'], `${path}.value`) };
}

function readRepeat(r: Reader, value: unknown, path: string): ObjectRepeat {
  const rec = r.record(value, path);
  const skip = rec['skip'] === undefined ? undefined : r.enumValue(rec['skip'], REPEAT_SKIP, `${path}.skip`, 'none');
  if (rec['kind'] === 'radial') {
    return {
      kind: 'radial',
      center: r.vec2(rec['center'], `${path}.center`),
      count: readBinding(r, rec['count'], `${path}.count`),
      startAngle: r.number(rec['startAngle'], `${path}.startAngle`),
      sweep: r.number(rec['sweep'], `${path}.sweep`, { min: -2 * Math.PI, max: 2 * Math.PI }),
      ...(skip !== undefined ? { skip } : {}),
    };
  }
  if (rec['kind'] === 'index') return { kind: 'index', count: readBinding(r, rec['count'], `${path}.count`) };
  if (rec['kind'] !== 'linear') r.fail(`${path}.kind`, 'must be linear, radial or index');
  const cell = r.vec2(rec['cell'], `${path}.cell`);
  if (cell[1] - cell[0] <= 1e-6) r.fail(`${path}.cell`, 'cell end must be after its start');
  const run = rec['run'] === undefined ? undefined : r.vec2(rec['run'], `${path}.run`);
  if (run && run[1] - run[0] <= 1e-6) r.fail(`${path}.run`, 'run end must be after its start');
  const pitch = rec['pitch'] === undefined ? undefined : readBinding(r, rec['pitch'], `${path}.pitch`);
  const count = rec['count'] === undefined ? undefined : readBinding(r, rec['count'], `${path}.count`);
  if (!pitch && !count) r.fail(path, 'needs a pitch or a count');
  if (pitch && 'value' in pitch && pitch.value <= 0) r.fail(`${path}.pitch.value`, 'must be > 0');
  const minCount = r.optionalNumber(rec['minCount'], `${path}.minCount`);
  const maxCount = r.optionalNumber(rec['maxCount'], `${path}.maxCount`);
  const mirrorAlternate = r.optionalBoolean(rec['mirrorAlternate'], `${path}.mirrorAlternate`);
  let runExpr: { start: string; end: string } | undefined;
  if (rec['runExpr'] !== undefined && rec['runExpr'] !== null) {
    const e = r.record(rec['runExpr'], `${path}.runExpr`);
    runExpr = { start: r.expr(e['start'], `${path}.runExpr.start`), end: r.expr(e['end'], `${path}.runExpr.end`) };
  }
  return {
    kind: 'linear',
    axis: r.enumValue(rec['axis'], ['u', 'n'] as const, `${path}.axis`, 'u'),
    cell,
    ...(run ? { run } : {}),
    ...(pitch ? { pitch } : {}),
    ...(count ? { count } : {}),
    fit: r.enumValue(rec['fit'], ['fill', 'exact'] as const, `${path}.fit`, 'fill'),
    cellResize: r.enumValue(rec['cellResize'], ['stretch', 'fixed'] as const, `${path}.cellResize`, 'stretch'),
    ...(skip !== undefined ? { skip } : {}),
    ...(mirrorAlternate !== undefined ? { mirrorAlternate } : {}),
    ...(minCount !== undefined ? { minCount } : {}),
    ...(maxCount !== undefined ? { maxCount } : {}),
    ...(runExpr ? { runExpr } : {}),
  };
}

function readModifier(r: Reader, value: unknown, path: string): ObjectModifier {
  const rec = r.record(value, path);
  const enabled = r.optionalBoolean(rec['enabled'], `${path}.enabled`);
  const on = enabled !== undefined ? { enabled } : {};
  if (rec['kind'] === 'roundCorners') {
    const radius = readBinding(r, rec['radius'], `${path}.radius`);
    if ('value' in radius && radius.value < 0) r.fail(`${path}.radius.value`, 'must be ≥ 0');
    return { kind: 'roundCorners', radius, ...on };
  }
  if (rec['kind'] !== 'mirror') r.fail(`${path}.kind`, 'must be mirror or roundCorners');
  return { kind: 'mirror', axis: r.enumValue(rec['axis'], ['u', 'n', 'both'] as const, `${path}.axis`, 'u'), ...on };
}

function readParamValue(r: Reader, value: unknown, path: string): ObjectParamValue {
  if (typeof value === 'string' || typeof value === 'boolean' || isFiniteNumber(value)) return value;
  r.fail(path, 'must be a number, string or boolean');
  return false;
}

function readGroup(r: Reader, value: unknown, path: string): ObjectGroup {
  const rec = r.record(value, path);
  const resize = r.record(rec['resize'], `${path}.resize`);
  let visibleWhen: ObjectVisibilityCondition | undefined;
  if (rec['visibleWhen'] !== undefined && rec['visibleWhen'] !== null) {
    const cond = r.record(rec['visibleWhen'], `${path}.visibleWhen`);
    visibleWhen = { param: r.string(cond['param'], `${path}.visibleWhen.param`), equals: readParamValue(r, cond['equals'], `${path}.visibleWhen.equals`) };
  }
  let offset: { u?: string; n?: string; rotate?: string } | undefined;
  if (rec['offset'] !== undefined && rec['offset'] !== null) {
    const o = r.record(rec['offset'], `${path}.offset`);
    offset = {};
    for (const key of ['u', 'n', 'rotate'] as const) if (o[key] !== undefined && o[key] !== '') offset[key] = r.expr(o[key], `${path}.offset.${key}`);
  }
  const visibleIf = rec['visibleIf'] !== undefined && rec['visibleIf'] !== null && rec['visibleIf'] !== '' ? r.expr(rec['visibleIf'], `${path}.visibleIf`) : undefined;
  return {
    id: r.id(rec['id'], `${path}.id`),
    name: r.string(rec['name'], `${path}.name`),
    shapeIds: r.array(rec['shapeIds'], `${path}.shapeIds`, OBJECT_DEFINITION_LIMITS.maxShapes).map((s, i) => r.id(s, `${path}.shapeIds[${i}]`)),
    resize: {
      u: r.enumValue(resize['u'], AXIS_RESIZE, `${path}.resize.u`, 'stretch'),
      n: r.enumValue(resize['n'], AXIS_RESIZE, `${path}.resize.n`, 'stretch'),
      ...(resize['uniform'] !== undefined && resize['uniform'] !== null ? { uniform: r.enumValue(resize['uniform'], ['width', 'depth', 'fit', 'fill'] as const, `${path}.resize.uniform`, 'fit') } : {}),
    },
    ...(rec['repeat'] !== undefined && rec['repeat'] !== null ? { repeat: readRepeat(r, rec['repeat'], `${path}.repeat`) } : {}),
    ...(visibleWhen ? { visibleWhen } : {}),
    ...(visibleIf ? { visibleIf } : {}),
    ...(offset && (offset.u || offset.n || offset.rotate) ? { offset } : {}),
    ...(Array.isArray(rec['modifiers']) && rec['modifiers'].length > 0
      ? { modifiers: r.array(rec['modifiers'], `${path}.modifiers`, OBJECT_DEFINITION_LIMITS.maxModifiers).map((m, i) => readModifier(r, m, `${path}.modifiers[${i}]`)) }
      : {}),
  };
}

function readAssignments(r: Reader, value: unknown, path: string): ObjectParamAssignment[] {
  return r.array(value, path, 16).map((a, i) => {
    const rec = r.record(a, `${path}[${i}]`);
    return { param: r.string(rec['param'], `${path}[${i}].param`, 32), expr: r.expr(rec['expr'], `${path}[${i}].expr`) };
  });
}

function readHandles(r: Reader, value: unknown): ObjectHandles {
  const rec = r.record(value, 'handles');
  const point = (v: unknown, path: string): ObjectHandlePoint => {
    if (isRecord(v)) return { u: r.expr(v['u'], `${path}.u`), n: r.expr(v['n'], `${path}.n`) };
    const p = r.vec2(v, path);
    if (Math.abs(p[0]) > 0.5 + 1e-9 || Math.abs(p[1]) > 0.5 + 1e-9) r.fail(path, 'must lie within the footprint ([-0.5, 0.5])');
    return p;
  };
  const depthHandle = (d: unknown, path: string): ObjectDepthHandle => {
    const h = r.record(d, path);
    const side = h['side'] === -1 ? -1 : 1;
    if (h['side'] !== 1 && h['side'] !== -1) r.fail(`${path}.side`, 'must be 1 or -1');
    let at: { u: string; n: string } | undefined;
    if (h['at'] !== undefined && h['at'] !== null) {
      const a = r.record(h['at'], `${path}.at`);
      at = { u: r.expr(a['u'], `${path}.at.u`), n: r.expr(a['n'], `${path}.at.n`) };
    }
    const u = at ? undefined : r.number(h['u'], `${path}.u`, { min: -0.5, max: 0.5 });
    const assign = h['assign'] !== undefined && h['assign'] !== null ? readAssignments(r, h['assign'], `${path}.assign`) : undefined;
    const label = optionalLabel(r, h['label'], `${path}.label`);
    return { side, ...(label ? { label } : {}), ...(u !== undefined ? { u } : {}), ...(at ? { at } : {}), ...(assign && assign.length > 0 ? { assign } : {}) };
  };
  return {
    ...(rec['start'] !== undefined ? { start: point(rec['start'], 'handles.start') } : {}),
    ...(rec['end'] !== undefined ? { end: point(rec['end'], 'handles.end') } : {}),
    ...(rec['depth'] !== undefined ? { depth: r.array(rec['depth'], 'handles.depth', 8).map((d, i) => depthHandle(d, `handles.depth[${i}]`)) } : {}),
  };
}

/** A rule's optional short name (labels of the editor's rules list). */
function optionalLabel(r: Reader, value: unknown, path: string): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  return r.string(value, path);
}

/** Formula notes explain a construction step: longer than labels. */
const MAX_FORMULA_NOTE_LENGTH = 200;

function readFormula(r: Reader, value: unknown, path: string): ObjectFormula {
  const rec = r.record(value, path);
  const key = r.string(rec['key'], `${path}.key`, 32);
  if (!OBJECT_PARAM_KEY_PATTERN.test(key)) r.fail(`${path}.key`, 'must start with a letter and only contain letters and digits');
  const note = rec['note'] === undefined || rec['note'] === '' ? undefined : r.string(rec['note'], `${path}.note`, MAX_FORMULA_NOTE_LENGTH);
  const label = optionalLabel(r, rec['label'], `${path}.label`);
  return { key, expr: r.expr(rec['expr'], `${path}.expr`), ...(label ? { label } : {}), ...(note !== undefined ? { note } : {}) };
}

function readStepSync(r: Reader, value: unknown, path: string): ObjectStepSync {
  const rec = r.record(value, path);
  const minCount = r.optionalNumber(rec['minCount'], `${path}.minCount`);
  const exactStep = r.optionalBoolean(rec['exactStep'], `${path}.exactStep`);
  const label = optionalLabel(r, rec['label'], `${path}.label`);
  return {
    ...(label ? { label } : {}),
    step: r.string(rec['step'], `${path}.step`, 32),
    count: r.string(rec['count'], `${path}.count`, 32),
    run: r.expr(rec['run'], `${path}.run`),
    ...(minCount !== undefined ? { minCount } : {}),
    ...(exactStep !== undefined ? { exactStep } : {}),
  };
}

function readDepthLink(r: Reader, value: unknown): ObjectDepthLink {
  const rec = r.record(value, 'depthLink');
  const onResize = rec['onResize'] !== undefined && rec['onResize'] !== null ? readAssignments(r, rec['onResize'], 'depthLink.onResize') : undefined;
  const label = optionalLabel(r, rec['label'], 'depthLink.label');
  return { expr: r.expr(rec['expr'], 'depthLink.expr'), ...(label ? { label } : {}), ...(onResize && onResize.length > 0 ? { onResize } : {}) };
}

function readModel3d(r: Reader, value: unknown): ObjectModel3D {
  const rec = r.record(value, 'model3d');
  const scaleMode = r.record(rec['scaleMode'], 'model3d.scaleMode');
  return {
    assetId: r.id(rec['assetId'], 'model3d.assetId'),
    position: r.vec3(rec['position'], 'model3d.position'),
    rotation: r.vec3(rec['rotation'], 'model3d.rotation'),
    scale: r.vec3(rec['scale'], 'model3d.scale'),
    ...(rec['pivot'] !== undefined && rec['pivot'] !== null ? { pivot: r.vec3(rec['pivot'], 'model3d.pivot') } : {}),
    scaleMode: {
      u: r.enumValue(scaleMode['u'], MODEL_SCALE_MODES, 'model3d.scaleMode.u', 'stretch'),
      n: r.enumValue(scaleMode['n'], MODEL_SCALE_MODES, 'model3d.scaleMode.n', 'stretch'),
      h: r.enumValue(scaleMode['h'], MODEL_SCALE_MODES, 'model3d.scaleMode.h', 'stretch'),
    },
  };
}

/** The formula fields a shape of this kind can have (see ObjectShapeBase.expr). */
export function shapeExpressionFields(shape: ObjectShape): string[] {
  switch (shape.kind) {
    case 'polyline':
      return shape.points.flatMap((_, i) => [`p${i}.u`, `p${i}.n`]);
    case 'rect':
      return ['min.u', 'min.n', 'max.u', 'max.n'];
    case 'circle':
      return ['center.u', 'center.n', 'radius'];
    case 'arc':
      return ['center.u', 'center.n', 'radius', 'startAngle', 'endAngle'];
    case 'path':
      return [];
  }
}

/**
 * Names an expression may read: the live size, every param except choices'
 * (booleans read 1/0, choices their option index) and the formulas declared
 * so far (`formulasUpTo`, default all).
 */
export function expressionNamesOf(spec: ObjectDefinitionSpec, formulasUpTo?: number): Set<string> {
  const formulas = spec.formulas ?? [];
  return new Set<string>([...sizeVariablesOf(spec), ...spec.params.map((p) => p.key), ...formulas.slice(0, formulasUpTo ?? formulas.length).map((f) => f.key)]);
}

/** The live-size names of a spec: width, depth, height — and `wall` for doors / windows, `offset` for dimensions. */
export function sizeVariablesOf(spec: Pick<ObjectDefinitionSpec, 'opening' | 'dimension'>): string[] {
  if (spec.opening) return [...EXPRESSION_SIZE_VARIABLES, OPENING_WALL_VARIABLE];
  if (spec.dimension) return [...EXPRESSION_SIZE_VARIABLES, DIMENSION_OFFSET_VARIABLE];
  return [...EXPRESSION_SIZE_VARIABLES];
}

/** Cross-reference checks on a structurally valid spec. */
function checkSemantics(r: Reader, spec: ObjectDefinitionSpec): void {
  const L = OBJECT_DEFINITION_LIMITS;
  const paramsByKey = new Map<string, ObjectParam>();
  const reserved = new Set<string>([...sizeVariablesOf(spec), ...EXPRESSION_RESERVED_NAMES]);
  spec.params.forEach((p, i) => {
    if (paramsByKey.has(p.key)) r.fail(`params[${i}].key`, `duplicate key "${p.key}"`);
    if (reserved.has(p.key)) r.fail(`params[${i}].key`, `"${p.key}" is a reserved name`);
    paramsByKey.set(p.key, p);
  });

  const expression = (src: string | undefined, at: string, names: ReadonlySet<string>): void => {
    const problem = src === undefined ? null : checkExpression(src, names);
    if (problem) r.fail(at, problem);
  };

  const formulaKeys = new Set<string>();
  (spec.formulas ?? []).forEach((f, i) => {
    const path = `formulas[${i}]`;
    if (paramsByKey.has(f.key) || formulaKeys.has(f.key)) r.fail(`${path}.key`, `duplicate key "${f.key}"`);
    if (reserved.has(f.key)) r.fail(`${path}.key`, `"${f.key}" is a reserved name`);
    // A formula reads the ones above it only (no cycles).
    expression(f.expr, `${path}.expr`, expressionNamesOf(spec, i));
    formulaKeys.add(f.key);
  });
  const names = expressionNamesOf(spec);
  const withIndex = new Set<string>([...names, 'i', 'count']);

  const shapeIds = new Set<string>();
  let pointCount = 0;
  spec.shapes.forEach((s, i) => {
    if (shapeIds.has(s.id)) r.fail(`shapes[${i}].id`, `duplicate id "${s.id}"`);
    shapeIds.add(s.id);
    pointCount += s.kind === 'polyline' ? s.points.length : s.kind === 'path' ? s.nodes.length * 3 : 2;
    if (s.expr) {
      const fields = new Set(shapeExpressionFields(s));
      for (const [field, src] of Object.entries(s.expr)) {
        if (!fields.has(field)) r.fail(`shapes[${i}].expr.${field}`, s.kind === 'path' ? 'curves take no formulas' : `a ${s.kind} has no field "${field}"`);
        else expression(src, `shapes[${i}].expr.${field}`, withIndex);
      }
    }
  });
  if (pointCount > L.maxPoints) r.fail('shapes', `too many points (${pointCount} > ${L.maxPoints})`);

  const binding = (b: NumberBinding | undefined, path: string, integer: boolean, bindingNames: ReadonlySet<string>): void => {
    if (!b) return;
    if ('expr' in b) {
      expression(b.expr, `${path}.expr`, bindingNames);
      return;
    }
    if (!('param' in b)) return;
    const p = paramsByKey.get(b.param);
    if (!p) r.fail(path, `unknown param "${b.param}"`);
    else if (integer ? p.kind !== 'int' : p.kind !== 'float' && p.kind !== 'int') r.fail(path, `param "${b.param}" must be ${integer ? 'an int' : 'numeric'}`);
  };
  const numericParam = (key: string, path: string, kind: 'int' | 'numeric'): void => {
    const p = paramsByKey.get(key);
    if (!p) r.fail(path, `unknown param "${key}"`);
    else if (kind === 'int' ? p.kind !== 'int' : p.kind !== 'float' && p.kind !== 'int') r.fail(path, `param "${key}" must be ${kind === 'int' ? 'an int' : 'numeric'}`);
  };

  const grouped = new Set<string>();
  const groupIds = new Set<string>();
  spec.groups.forEach((g, i) => {
    const path = `groups[${i}]`;
    if (groupIds.has(g.id)) r.fail(`${path}.id`, `duplicate id "${g.id}"`);
    groupIds.add(g.id);
    g.shapeIds.forEach((id, j) => {
      if (!shapeIds.has(id)) r.fail(`${path}.shapeIds[${j}]`, `unknown shape "${id}"`);
      if (grouped.has(id)) r.fail(`${path}.shapeIds[${j}]`, `shape "${id}" is already in another group`);
      grouped.add(id);
    });
    if (g.repeat?.kind === 'linear') {
      binding(g.repeat.pitch, `${path}.repeat.pitch`, false, names);
      binding(g.repeat.count, `${path}.repeat.count`, true, names);
      if (g.repeat.count && 'value' in g.repeat.count && (g.repeat.count.value < 1 || g.repeat.count.value > L.maxRepeatCount)) {
        r.fail(`${path}.repeat.count.value`, `must be between 1 and ${L.maxRepeatCount}`);
      }
      if (g.repeat.runExpr) {
        expression(g.repeat.runExpr.start, `${path}.repeat.runExpr.start`, names);
        expression(g.repeat.runExpr.end, `${path}.repeat.runExpr.end`, names);
      }
    } else if (g.repeat?.kind === 'radial') {
      binding(g.repeat.count, `${path}.repeat.count`, true, names);
    } else if (g.repeat?.kind === 'index') {
      binding(g.repeat.count, `${path}.repeat.count`, true, names);
      if ('value' in g.repeat.count && (g.repeat.count.value < 0 || g.repeat.count.value > L.maxRepeatCount)) r.fail(`${path}.repeat.count.value`, `must be between 0 and ${L.maxRepeatCount}`);
    }
    if (g.visibleWhen && !paramsByKey.has(g.visibleWhen.param)) r.fail(`${path}.visibleWhen.param`, `unknown param "${g.visibleWhen.param}"`);
    expression(g.visibleIf, `${path}.visibleIf`, withIndex);
    // Offsets may place each copy of an index repeat.
    const offsetNames = g.repeat?.kind === 'index' ? withIndex : names;
    expression(g.offset?.u, `${path}.offset.u`, offsetNames);
    expression(g.offset?.n, `${path}.offset.n`, offsetNames);
    expression(g.offset?.rotate, `${path}.offset.rotate`, offsetNames);
    g.modifiers?.forEach((m, j) => {
      if (m.kind === 'roundCorners') binding(m.radius, `${path}.modifiers[${j}].radius`, false, names);
    });
  });

  const assignments = (list: readonly ObjectParamAssignment[] | undefined, path: string, assignNames: ReadonlySet<string>): void => {
    list?.forEach((a, i) => {
      numericParam(a.param, `${path}[${i}].param`, 'numeric');
      expression(a.expr, `${path}[${i}].expr`, assignNames);
    });
  };

  const handleNames = new Set<string>([...names, 'pulled']);
  const point = (p: ObjectHandlePoint | undefined, path: string): void => {
    if (!p || !isFormulaHandlePoint(p)) return;
    expression(p.u, `${path}.u`, names);
    expression(p.n, `${path}.n`, names);
  };
  point(spec.handles?.start, 'handles.start');
  point(spec.handles?.end, 'handles.end');
  spec.handles?.depth?.forEach((d, i) => {
    if (d.at) {
      expression(d.at.u, `handles.depth[${i}].at.u`, names);
      expression(d.at.n, `handles.depth[${i}].at.n`, names);
    }
    assignments(d.assign, `handles.depth[${i}].assign`, handleNames);
  });

  (spec.stepSyncs ?? []).forEach((sync, i) => {
    const path = `stepSyncs[${i}]`;
    numericParam(sync.step, `${path}.step`, 'numeric');
    numericParam(sync.count, `${path}.count`, 'int');
    expression(sync.run, `${path}.run`, names);
  });
  if (spec.depthLink) {
    expression(spec.depthLink.expr, 'depthLink.expr', names);
    assignments(spec.depthLink.onResize, 'depthLink.onResize', names);
  }

  if (spec.drawLikeWall && (spec.anchor !== 'LINE' || spec.constraint.kind !== 'FREE')) {
    r.fail('drawLikeWall', 'only LINE objects with a FREE constraint can be drawn like a wall');
  }
  if (spec.handles && spec.anchor !== 'LINE') r.fail('handles', 'resize handles are only available on LINE objects');

  // Doors / windows follow their opening: no handles, linked depth, wall-style drawing or 3D model of their own.
  if (spec.opening) {
    if (spec.anchor !== 'LINE' || spec.constraint.kind !== 'WALL') r.fail('opening', 'doors and windows sit in a wall opening (LINE anchor, WALL constraint)');
    if (spec.handles) r.fail('handles', 'doors and windows resize with their opening: no handles');
    if (spec.depthLink) r.fail('depthLink', 'doors and windows have no depth of their own');
    if (spec.drawLikeWall) r.fail('drawLikeWall', 'doors and windows are not drawn like a wall');
    if (spec.model3d) r.fail('model3d', 'doors and windows have no 3D model');
  }

  // Dimensions show the measured length between their two endpoints: only the ends move (params set their look).
  if (spec.dimension) {
    if (spec.opening) r.fail('dimension', 'a dimension is not a door or window');
    if (spec.anchor !== 'LINE' || spec.constraint.kind !== 'FREE' || !spec.drawLikeWall) r.fail('dimension', 'dimensions are drawn between two points (LINE anchor, FREE constraint, drawn like a wall)');
    if (spec.handles) r.fail('handles', 'dimensions are resized by their endpoints: no handles');
    if (spec.depthLink) r.fail('depthLink', 'dimensions have no depth of their own');
    if (spec.stepSyncs?.length) r.fail('stepSyncs', 'dimensions have no linked values');
    if (spec.model3d) r.fail('model3d', 'dimensions have no 3D model');
  }
}

function readOpening(r: Reader, value: unknown): ObjectOpening {
  const rec = r.record(value, 'opening');
  return { kind: r.enumValue(rec['kind'], ['DOOR', 'WINDOW'] as const, 'opening.kind', 'DOOR') };
}

function readDimension(r: Reader, value: unknown): ObjectDimension {
  const rec = r.record(value, 'dimension');
  return { kind: r.enumValue(rec['kind'], ['LINEAR'] as const, 'dimension.kind', 'LINEAR') };
}

/**
 * Parse untrusted input into a typed {@link ObjectDefinitionSpec}. Unknown
 * keys are dropped; any issue fails the whole parse (issues are complete, not
 * first-error-only, so the editor can list them all).
 */
export function parseObjectDefinitionSpec(input: unknown): ObjectDefinitionParseResult {
  const r = new Reader();
  const L = OBJECT_DEFINITION_LIMITS;
  const rec = r.record(input, '');
  if (!isRecord(input)) return { ok: false, issues: r.issues };

  if (rec['schemaVersion'] !== OBJECT_DEFINITION_SCHEMA_VERSION) r.fail('schemaVersion', `must be ${OBJECT_DEFINITION_SCHEMA_VERSION}`);
  const size = r.record(rec['defaultSize'], 'defaultSize');
  const sizeOpts = { min: L.minSize, max: L.maxSize, fallback: 1 };
  const handlesInput = rec['handles'];
  const modelInput = rec['model3d'];

  const spec: ObjectDefinitionSpec = {
    schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
    anchor: r.enumValue(rec['anchor'], ['POINT', 'LINE'] as const, 'anchor', 'POINT'),
    constraint: readConstraint(r, rec['constraint']),
    ...(rec['drawLikeWall'] !== undefined ? { drawLikeWall: r.boolean(rec['drawLikeWall'], 'drawLikeWall') } : {}),
    defaultSize: {
      width: r.number(size['width'], 'defaultSize.width', sizeOpts),
      depth: r.number(size['depth'], 'defaultSize.depth', sizeOpts),
      height: r.number(size['height'], 'defaultSize.height', sizeOpts),
    },
    params: r.array(rec['params'], 'params', L.maxParams).map((p, i) => readParam(r, p, `params[${i}]`)),
    ...(Array.isArray(rec['formulas']) && rec['formulas'].length > 0 ? { formulas: r.array(rec['formulas'], 'formulas', L.maxFormulas).map((f, i) => readFormula(r, f, `formulas[${i}]`)) } : {}),
    shapes: r.array(rec['shapes'], 'shapes', L.maxShapes).map((s, i) => readShape(r, s, `shapes[${i}]`)),
    groups: r.array(rec['groups'], 'groups', L.maxGroups).map((g, i) => readGroup(r, g, `groups[${i}]`)),
    ...(handlesInput !== undefined && handlesInput !== null ? { handles: readHandles(r, handlesInput) } : {}),
    ...(Array.isArray(rec['stepSyncs']) && rec['stepSyncs'].length > 0 ? { stepSyncs: r.array(rec['stepSyncs'], 'stepSyncs', 8).map((x, i) => readStepSync(r, x, `stepSyncs[${i}]`)) } : {}),
    ...(rec['depthLink'] !== undefined && rec['depthLink'] !== null ? { depthLink: readDepthLink(r, rec['depthLink']) } : {}),
    model3d: modelInput !== undefined && modelInput !== null ? readModel3d(r, modelInput) : null,
    ...(rec['opening'] !== undefined && rec['opening'] !== null ? { opening: readOpening(r, rec['opening']) } : {}),
    ...(rec['dimension'] !== undefined && rec['dimension'] !== null ? { dimension: readDimension(r, rec['dimension']) } : {}),
  };

  if (r.issues.length === 0) checkSemantics(r, spec);
  return r.issues.length === 0 ? { ok: true, spec } : { ok: false, issues: r.issues };
}

/** Convenience for already-typed specs (editor state): the issues, empty when valid. */
export function validateObjectDefinitionSpec(spec: ObjectDefinitionSpec): ObjectDefinitionIssue[] {
  const result = parseObjectDefinitionSpec(JSON.parse(JSON.stringify(spec)));
  // `in` narrowing: libs compiling this file without strictNullChecks can't narrow on `ok`.
  return 'issues' in result ? result.issues : [];
}
